import React, { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { cardReveal, fadeUp, Reveal, staggerContainer, viewportOnce } from "../common/Motion";

const industries = [
  ["Real estate", "Qualify buyers and tenants, answer property questions, and arrange viewings around the clock."],
  ["Restaurants", "Handle menu questions, table requests, branch information, and customer follow-up in one conversation."],
  ["Order booking", "Guide customers through product selection, capture complete orders, and pass them to fulfilment teams."],
  ["Healthcare", "Answer service questions, collect non-clinical intake details, and help patients request appointments."],
  ["Education", "Respond to programme enquiries, explain admissions, and turn prospective students into qualified leads."],
  ["Professional services", "Understand client needs, answer common questions, and route each enquiry to the right specialist."],
];

const demos = [
  {
    industry: "Real estate",
    title: "Property enquiry assistant",
    messages: [
      ["assistant", "Hi Sara — tell me what kind of property you are looking for."],
      ["customer", "A two-bedroom apartment in Clifton, within PKR 90,000 per month."],
      ["assistant", "I found matching listings. I can share the shortlist and arrange a viewing with an available agent."],
    ],
    actions: ["Lead qualified", "Viewing request ready"],
    placeholder: "Ask about a property…",
  },
  {
    industry: "Dental clinic",
    title: "Appointment booking assistant",
    messages: [
      ["assistant", "Welcome to Pearl Dental. Which service and day would work best for you?"],
      ["customer", "I need a cleaning appointment on Tuesday afternoon."],
      ["assistant", "Dr. Ahmed has openings at 2:30 PM and 4:00 PM. Which time should I reserve?"],
    ],
    actions: ["2:30 PM available", "4:00 PM available"],
    placeholder: "Choose a time slot…",
  },
  {
    industry: "Restaurant",
    title: "Order booking assistant",
    messages: [
      ["assistant", "Good evening! I can help with the menu, dietary questions, or place your order."],
      ["customer", "One chicken karahi, two garlic naans, and raita for delivery."],
      ["assistant", "Your order is ready to confirm. Please share your delivery area and preferred payment method."],
    ],
    actions: ["3 items added", "Order ready to confirm"],
    placeholder: "Add to your order…",
  },
];

const ChatbotProduct = () => {
  const reduceMotion = useReducedMotion();
  const [activeDemo, setActiveDemo] = useState(0);
  const demo = demos[activeDemo];

  useEffect(() => {
    if (reduceMotion) return;
    const timer = window.setInterval(() => setActiveDemo((current) => (current + 1) % demos.length), 10000);
    return () => window.clearInterval(timer);
  }, [reduceMotion]);

  const moveDemo = (direction: number) => setActiveDemo((current) => (current + direction + demos.length) % demos.length);
  const openAssistant = () => {
    const launcher = document.querySelector<HTMLButtonElement>(".support-launcher");
    launcher?.click();
  };

  return <section id="ai-assistant" className="section chatbot-product-section">
    <div className="site-shell">
      <Reveal className="chatbot-product-hero">
        <div className="chatbot-product-copy">
          <p className="eyebrow">AI customer assistant</p>
          <h2>Turn every conversation into useful action.</h2>
          <p>Visiosol’s AI-powered assistant gives customers immediate, grounded answers, captures qualified leads, supports appointment requests, and hands the full context to your team when a human should take over.</p>
          <div className="chatbot-feature-list"><span>24/7 grounded answers</span><span>Lead qualification</span><span>Appointment requests</span><span>Human handoff</span><span>Website & WhatsApp ready</span><span>Conversation insights</span></div>
          <div className="chatbot-actions">
            <button className="button button-primary" type="button" onClick={openAssistant}>Try the live assistant <span aria-hidden="true">↗</span></button>
            <a className="button button-secondary" href="#contact">Discuss your use case</a>
          </div>
        </div>
        <motion.div className="chatbot-demo" variants={reduceMotion ? undefined : fadeUp}>
          <div className="chatbot-demo-header"><span className="chatbot-avatar">AI</span><div><strong>{demo.title}</strong><small><i></i> Online · Human help available</small></div><div className="demo-slider-arrows"><button type="button" onClick={() => moveDemo(-1)} aria-label="Previous industry example">←</button><button type="button" onClick={() => moveDemo(1)} aria-label="Next industry example">→</button></div></div>
          <div className="demo-slider-tabs" role="tablist" aria-label="Chatbot industry examples">{demos.map((item, index) => <button type="button" role="tab" aria-selected={activeDemo === index} className={activeDemo === index ? "active" : ""} onClick={() => setActiveDemo(index)} key={item.industry}>{item.industry}</button>)}</div>
          <AnimatePresence mode="wait" initial={false}>
            <motion.div className="chatbot-demo-body" key={demo.industry} initial={reduceMotion ? false : { opacity: 0, x: 18 }} animate={{ opacity: 1, x: 0 }} exit={reduceMotion ? undefined : { opacity: 0, x: -18 }} transition={{ duration: .28 }}>
              {demo.messages.map(([role, message], index) => <p className={`demo-message ${role}`} key={`${demo.industry}-${index}`}>{message}</p>)}
              <div className="demo-action-row">{demo.actions.map((action) => <span key={action}>{action}</span>)}</div>
            </motion.div>
          </AnimatePresence>
          <div className="chatbot-demo-input"><span>{demo.placeholder}</span><b>↑</b></div>
        </motion.div>
      </Reveal>

      <Reveal className="chatbot-usecase-heading"><p className="eyebrow">Built around your industry</p><h3>One intelligent conversation layer. Many ways to use it.</h3></Reveal>
      <motion.div className="industry-grid" variants={reduceMotion ? undefined : staggerContainer} initial={reduceMotion ? false : "hidden"} whileInView="visible" viewport={viewportOnce}>
        {industries.map(([title, copy], index) => <motion.article variants={cardReveal} key={title}><span className="industry-icon" aria-hidden="true">{String(index + 1).padStart(2, "0")}</span><h4>{title}</h4><p>{copy}</p></motion.article>)}
      </motion.div>
      <Reveal className="chatbot-product-note"><div><span>Not a generic chatbot</span><strong>Configured around your knowledge, workflows, tone, and escalation rules.</strong></div><a href="#contact">Plan your AI assistant <span>→</span></a></Reveal>
    </div>
  </section>;
};

export default ChatbotProduct;
